import { normalizeApiResponse, normalizeSingleEntityResponse } from '@/utils/apiResponseNormalizer'
import { authService } from '../auth/authService'
import { notifySessionExpired } from '../auth/sessionEvents'
import { API_BASE_URL, API_RETRY, API_RETRY_ENABLED, API_TIMEOUTS } from './apiConfig'

// User-facing messages for statuses the UI is expected to surface. 404 does
// not distinguish "missing" from "outside your scope": the backend returns
// the same status for both, and so must the message.
const STATUS_MESSAGES = {
  401: 'Your session has expired. Please sign in again.',
  403: "You don't have access to this resource.",
  404: "This item doesn't exist or isn't available to your account.",
}

/**
 * Custom error class for API errors
 * @internal - Used internally by apiService, not exported for external use
 */
class ApiError extends Error {
  constructor(message, status, data = {}) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

/**
 * Service for handling API requests
 */
export const apiService = {
  /**
   * Make an API request with optional authentication
   * @param {string} endpoint - API endpoint
   * @param {object} options - Fetch options
   * @returns {Promise<object>} Response data
   */
  async request(endpoint, options = {}) {
    // Convert API_BASE_URL to string when used (lazy evaluation)
    // Explicitly call toString() to ensure the lazy getter runs
    const baseUrl = API_BASE_URL.toString()
    let url = endpoint.startsWith('http') ? endpoint : `${baseUrl}${endpoint}`

    // Handle query parameters
    if (options.params) {
      const searchParams = new URLSearchParams()
      Object.entries(options.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value)
        }
      })
      const queryString = searchParams.toString()
      if (queryString) {
        url += (url.includes('?') ? '&' : '?') + queryString
      }
    }

    // Prepare headers
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    }

    // Add auth token if required (default is true)
    const requireAuth = options.requireAuth !== false
    if (requireAuth) {
      const token = await authService.getAuthToken()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
    }

    // Setup AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || API_TIMEOUTS.DEFAULT)

    // Make request with retry logic - track retries across recursive calls
    // Note: Using _retryCount in options to persist retry state across recursive calls
    const currentRetryCount = options._retryCount || 0
    const maxRetries = options.maxRetries || API_RETRY.MAX_RETRIES
    const shouldRetry = options.retry ?? API_RETRY_ENABLED

    try {
      // Add signal to options
      options.signal = controller.signal

      // Add headers to options
      options.headers = headers

      // Make request
      const response = await fetch(url, options)

      // Clear timeout
      clearTimeout(timeoutId)

      // Handle non-2xx responses
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))

        // 401: refresh the session once and replay; if that fails the session
        // is gone and the auth layer signs the user out. 403 is never retried.
        if (response.status === 401 && requireAuth) {
          if (!options._authRetried && (await authService.refreshSession())) {
            return this.request(endpoint, { ...options, _authRetried: true })
          }
          notifySessionExpired()
        }

        throw new ApiError(
          STATUS_MESSAGES[response.status] ||
            errorData.message ||
            errorData.detail ||
            `HTTP Error: ${response.status}`,
          response.status,
          errorData
        )
      }

      // Parse response body
      let data
      const contentType = response.headers.get('content-type')
      if (contentType?.includes('application/json')) {
        data = await response.json()
      } else {
        data = await response.text()
      }

      return {
        data,
        status: response.status,
        headers: response.headers,
      }
    } catch (error) {
      // Clear timeout
      clearTimeout(timeoutId)

      // Handle network errors (TypeError from fetch failures)
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        const networkError = new ApiError('Network error - please check your connection', 0)
        // Don't retry network errors immediately, but allow retry logic to handle it
        if (shouldRetry && currentRetryCount < maxRetries) {
          const nextRetryCount = currentRetryCount + 1
          const delay = Math.min(
            API_RETRY.INITIAL_DELAY * 2 ** currentRetryCount,
            API_RETRY.MAX_DELAY
          )
          await new Promise((resolve) => setTimeout(resolve, delay))
          const retryOptions = {
            ...options,
            _retryCount: nextRetryCount,
          }
          return this.request(endpoint, retryOptions)
        }
        throw networkError
      }

      // Handle AbortError specifically (timeout)
      if (error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408)
      }

      // Handle retry logic for other errors
      if (
        shouldRetry &&
        currentRetryCount < maxRetries &&
        error.name !== 'AbortError' &&
        // Don't retry authentication errors or server errors that are likely auth-related
        (!error.status || API_RETRY.RETRYABLE_STATUS_CODES.includes(error.status))
      ) {
        const nextRetryCount = currentRetryCount + 1

        // Exponential backoff: 1s, 2s, 4s, 8s...
        const delay = Math.min(
          API_RETRY.INITIAL_DELAY * 2 ** currentRetryCount,
          API_RETRY.MAX_DELAY
        )
        await new Promise((resolve) => setTimeout(resolve, delay))

        // Pass updated retry count to recursive call
        const retryOptions = {
          ...options,
          _retryCount: nextRetryCount,
        }
        return this.request(endpoint, retryOptions)
      }

      // Re-throw error (may be ApiError or other error type)
      throw error
    }
  },

  /**
   * Make a GET request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional options
   * @param {boolean} options.normalize - Whether to normalize the response (default: false)
   * @param {string} options.entityType - Entity type for normalization context
   * @param {boolean} options.isSingleEntity - Whether expecting a single entity response
   * @returns {Promise<object>} Response data (normalized if requested)
   */
  async get(endpoint, options = {}) {
    const {
      normalize = false,
      entityType = 'entity',
      isSingleEntity = false,
      ...apiOptions
    } = options

    const response = await this.request(endpoint, {
      method: 'GET',
      ...apiOptions,
    })

    // Apply normalization if requested
    if (normalize) {
      if (isSingleEntity) {
        return normalizeSingleEntityResponse(response.data, entityType)
      } else {
        return normalizeApiResponse(response.data, apiOptions.params || {})
      }
    }

    // Return the data portion of the response (maintaining backward compatibility)
    return response.data
  },

  /**
   * Make a POST request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} options - Additional options
   * @returns {Promise<object>} Response data
   */
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    })
  },

  /**
   * Make a PUT request
   * @param {string} endpoint - API endpoint
   * @param {object} data - Request body data
   * @param {object} options - Additional options
   * @returns {Promise<object>} Response data
   */
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    })
  },

  /**
   * Make a DELETE request
   * @param {string} endpoint - API endpoint
   * @param {object} options - Additional options
   * @returns {Promise<object>} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    })
  },

  /**
   * Make a normalized GET request for list endpoints
   * @param {string} endpoint - API endpoint
   * @param {object} options - Request options
   * @param {string} entityType - Entity type for normalization
   * @returns {Promise<object>} Normalized response with items array
   */
  async getNormalized(endpoint, options = {}, entityType = 'entity') {
    return this.get(endpoint, {
      ...options,
      normalize: true,
      entityType,
      isSingleEntity: false,
    })
  },

  /**
   * Make a normalized GET request for single entity endpoints
   * @param {string} endpoint - API endpoint
   * @param {object} options - Request options
   * @param {string} entityType - Entity type for normalization
   * @returns {Promise<object>} Normalized single entity response
   */
  async getSingleNormalized(endpoint, options = {}, entityType = 'entity') {
    return this.get(endpoint, {
      ...options,
      normalize: true,
      entityType,
      isSingleEntity: true,
    })
  },
}
